import {
  AdIcon,
  CalendarTimeIcon,
  ClickIcon,
  ExclamationIcon,
  LinkIcon,
  NoEntryIcon,
  PercentIcon,
  ZapIcon,
} from '@hprofits/ui';
import type { Component } from 'vue';

import { t } from '@/i18n';

/**
 * The legacy dictionary (`/dict/blacklist-condition`) names condition parameters by their API
 * keys. The enum keeps the known set typed; the UI shows localized words and keeps the key as
 * the stored/filtered value everywhere. An unknown key (the dictionary grew) falls back to
 * itself, so a new parameter stays visible instead of blank.
 */
export const CONDITION_PARAMETERS = [
  'show',
  'nurl',
  'lost_percent',
  'state',
  'change_state_hours',
  'profit',
  'fraud',
  'fraud_percent',
  'ban_count',
] as const;
export type ConditionParameter = (typeof CONDITION_PARAMETERS)[number];

const CONDITION_PARAMETER_LABEL_KEYS: Record<ConditionParameter, string> = {
  show: 'shows',
  nurl: 'nurl',
  lost_percent: 'lostPercent',
  state: 'state',
  change_state_hours: 'changeStateHours',
  profit: 'profit',
  fraud: 'fraud',
  fraud_percent: 'fraudPercent',
  ban_count: 'banCount',
};

/** Dropdown glyph per parameter — picked to read as the metric at a glance. */
const CONDITION_PARAMETER_ICONS: Record<ConditionParameter, Component> = {
  show: ClickIcon,
  nurl: LinkIcon,
  lost_percent: PercentIcon,
  state: NoEntryIcon,
  change_state_hours: CalendarTimeIcon,
  profit: AdIcon,
  fraud: ExclamationIcon,
  fraud_percent: PercentIcon,
  ban_count: ZapIcon,
};

const isConditionParameter = (parameter: string): parameter is ConditionParameter =>
  (CONDITION_PARAMETERS as ReadonlyArray<string>).includes(parameter);

export const conditionParameterLabel = (parameter: string): string =>
  isConditionParameter(parameter)
    ? t(`modules.blacklistAutoRules.editor.parameters.${CONDITION_PARAMETER_LABEL_KEYS[parameter]}`)
    : parameter;

/** Percent-scale parameters — their thresholds read as "5%", not "5". */
const PERCENT_PARAMETERS: ReadonlySet<ConditionParameter> = new Set(['lost_percent', 'fraud_percent']);
export const isPercentParameter = (parameter: string): boolean =>
  isConditionParameter(parameter) && PERCENT_PARAMETERS.has(parameter);

/** Option popover explanation: what the parameter compares, so a pick needs no tribal knowledge. */
export const conditionParameterDescription = (parameter: string): string | undefined =>
  isConditionParameter(parameter)
    ? t(`modules.blacklistAutoRules.editor.parameters.${CONDITION_PARAMETER_LABEL_KEYS[parameter]}Hint`)
    : undefined;

export const conditionParameterIcon = (parameter: string): Component | undefined =>
  isConditionParameter(parameter) ? CONDITION_PARAMETER_ICONS[parameter] : undefined;

/** Localizes the leading parameter token of a raw condition string ("nurl > 300" → "Nurl > 300")
 * and, for percent parameters, suffixes the threshold with "%" ("lost_percent > 1" → "Lost percent > 1%"). */
export const localizeConditionParam = (condition: string): string => {
  const match = /^([a-zA-Z_][\w.]*)/.exec(condition);
  if (match === null) {
    return condition;
  }
  const parameter = match[0];
  const rest = condition.slice(parameter.length);
  const label = conditionParameterLabel(parameter);
  if (!isPercentParameter(parameter)) {
    return label + rest;
  }
  // Trailing digit/dot run found by a plain reverse scan — regex-free to keep the linter's
  // slow-regex gate happy — then validated as a single number.
  let end = rest.length;
  while (end > 0) {
    const char = rest[end - 1];
    if (char !== undefined && '0123456789.'.includes(char)) {
      end -= 1;
    } else {
      break;
    }
  }
  const tail = rest.slice(end);
  if (tail === '' || Number.isNaN(Number(tail))) {
    return label + rest;
  }
  return `${label}${rest.slice(0, end)}${tail}%`;
};

// Significant digits preserve the API number's precision even for thresholds below 0.01.
const MAX_SIGNIFICANT_DIGITS = 21;
const numberFormatter = new Intl.NumberFormat('en-US', { maximumSignificantDigits: MAX_SIGNIFICANT_DIGITS });
export const formatConditionValue = (value: number | string | null): string => {
  if (value === null || value === '') {
    return '';
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numberFormatter.format(numeric) : String(value);
};
